import { Checkbox, CircularProgress, IconButton, TableCell, TableRow, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { getDownloadURL, ref } from 'firebase/storage';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import { Voter } from '../../types';

interface PrecinctVoterTableRowProps {
  voter: Voter;
  index: number;
  isSelected: boolean;
  onSelectVoter: (voterId: string) => void;
}

const PrecinctVoterTableRow: React.FC<PrecinctVoterTableRowProps> = ({
  voter, 
  index, 
  isSelected, 
  onSelectVoter
}) => {
  const [qrDocuments, setQRDocuments] = useState<any[]>([]);
  const [isLoadingQRDocuments, setIsLoadingQRDocuments] = useState(true);
  const [isStubReleased, setIsStubReleased] = useState(false);
  const [isFinalReleased, setIsFinalReleased] = useState(false);
  const [isExitPolled, setIsExitPolled] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  useEffect(() => {
    const fetchVoterStatus = async () => {
      try {
        setIsLoadingStatus(true);

        // Check Stub Releases
        const stublReleasesQuery = query(
          collection(db, 'stubReleases'), 
          where('voterId', '==', voter.id)
        );
        const stubReleasesSnapshot = await getDocs(stublReleasesQuery);
        setIsStubReleased(!stubReleasesSnapshot.empty);
        
        // Check Final Releases
        const finalReleasesQuery = query(
          collection(db, 'finalReleases'), 
          where('voterId', '==', voter.id)
        );
        const finalReleasesSnapshot = await getDocs(finalReleasesQuery);
        setIsFinalReleased(!finalReleasesSnapshot.empty);

        // Check Exit Polls
        const exitPollsQuery = query(
          collection(db, 'exitPolls'), 
          where('voterId', '==', voter.id)
        );
        const exitPollsSnapshot = await getDocs(exitPollsQuery);
        setIsExitPolled(!exitPollsSnapshot.empty);
      } catch (error) {
        console.error('Error checking voter status:', error);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    const fetchQRDocuments = async () => {
      try {
        setIsLoadingQRDocuments(true);
        const qrDocsRef = collection(db, 'qr_documents');
        const q = query(qrDocsRef, where('voters', 'array-contains', { id: voter.id, name: voter.name }));
        
        const querySnapshot = await getDocs(q);
        const documents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setQRDocuments(documents);
      } catch (error) {
        console.error('Error fetching QR documents:', error);
      } finally {
        setIsLoadingQRDocuments(false);
      }
    };

    fetchVoterStatus();
    fetchQRDocuments();
  }, [voter.id, voter.name]);

  const handleDownloadDocument = async (storagePath: string, filename: string) => {
    try {
      const fileRef = ref(storage, storagePath);
      const downloadURL = await getDownloadURL(fileRef);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  // Count and format QR documents
  const qrDocumentCount = qrDocuments.length;
  const qrDocumentDisplay = isLoadingQRDocuments 
    ? 'Loading documents...' 
    : (qrDocumentCount > 0 
      ? `${qrDocumentCount} document${qrDocumentCount !== 1 ? 's' : ''}`
      : 'No documents');

  return (
    <TableRow key={voter.id}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={() => onSelectVoter(voter.id)}
        />
      </TableCell>
      <TableCell>{index + 1}</TableCell>
      <TableCell>{voter.name}</TableCell>
      <TableCell>{voter.id}</TableCell>
      <TableCell>
        <Tooltip 
          title={isLoadingQRDocuments 
            ? 'Loading QR documents...' 
            : (qrDocumentCount > 0 
              ? qrDocuments.map(doc => doc.filename).join(', ') 
              : 'No QR documents generated')
          }
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isLoadingQRDocuments ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null}
            <span>{qrDocumentDisplay}</span>
            {!isLoadingQRDocuments && qrDocuments.map((doc, index) => (
              <Tooltip key={doc.id} title={`Download ${doc.filename}`}>
                <IconButton 
                  size="small" 
                  onClick={() => handleDownloadDocument(doc.storagePath, doc.filename)}
                  sx={{ ml: 1 }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
          </div>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip title={isLoadingStatus ? 'Loading status...' : 'Stub Released Status'}>
          {isLoadingStatus ? (
            <CircularProgress size={20} />
          ) : isStubReleased ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          )}
        </Tooltip>
      </TableCell>      
      <TableCell>
        <Tooltip title={isLoadingStatus ? 'Loading status...' : 'Final Released Status'}>
          {isLoadingStatus ? (
            <CircularProgress size={20} />
          ) : isFinalReleased ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          )}
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip title={isLoadingStatus ? 'Loading status...' : 'Exit Polled Status'}>
          {isLoadingStatus ? (
            <CircularProgress size={20} />
          ) : isExitPolled ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          )}
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default PrecinctVoterTableRow;
